import React, { useRef, useState } from 'react';

import ChevronDownIcon from 'icons/chevron-down.svg';
import ChevronUpIcon from 'icons/chevron-up.svg';
import DashboardIcon from 'icons/dashboard-sm.svg';
import HomeIcon from 'icons/home-sm.svg';
import AddedIcon from 'icons/ic-added.svg';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';

import { useInfiniteDivisionSearchesQuery } from 'components/hooks/divisionSearchQueries';

import { useCurrentActor } from 'components/contexts/CurrentActor';

const optionIconProps = {
  className: '[&_path]:tw-fill-grey-900',
  height: 20,
  width: 20,
};

const dashboardPath = '/dashboard';

export default function ContextSwitcher({ isDashboardAccessible }) {
  const {
    division: { id: currentDivisionId },
  } = useCurrentActor();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const [isDomainDivisionsFetchEnabled, setIsDomainDivisionsFetchEnabled] =
    useState(false);

  const [searchParam, setSearchParam] = useState('');

  const {
    data: domainDivisions,
    fetchNextPage: fetchNextDomainDivisionsPage,
    hasNextPage: hasNextDomainDivisionsPage,
    isFetching: isDomainDivisionsFetching,
    isLoading: isDomainDivisionsLoading,
  } = useInfiniteDivisionSearchesQuery({
    params: { search: searchParam },
    queryOptions: { enabled: isDomainDivisionsFetchEnabled },
  });

  const handleInputChange = useRef(
    _.debounce((searchText) => setSearchParam(searchText), 500),
  ).current;

  const handleChange = (option) => {
    window.location.href = option.path;
  };

  const options = (() => {
    if (!domainDivisions) return [];

    const isDashboardPath = window.location.pathname === dashboardPath;

    return [
      isDashboardAccessible &&
        new RegExp(searchParam, 'i').test('dashboard') && {
          icon: <DashboardIcon {...optionIconProps} />,
          isCurrent: isDashboardPath,
          label: i18nTShared('dashboard'),
          path: dashboardPath,
          value: 'dashboard',
        },
      domainDivisions.data.map((division) => ({
        icon:
          division.attributes.primary ?
            <HomeIcon {...optionIconProps} />
          : null,
        isCurrent: !isDashboardPath && division.id === currentDivisionId,
        label: division.attributes.name,
        path: `/companies/${division.id}`,
        value: `company-${division.id}`,
      })),
    ]
      .flat()
      .filter(Boolean);
  })();

  return (
    <div className='tw-relative'>
      <Select
        autoFocus={false}
        className='context-switcher'
        classNamePrefix='context-switcher'
        components={{ DropdownIndicator, Option, Placeholder }}
        controlShouldRenderValue={true}
        filterOption={() => true}
        isLoading={isDomainDivisionsLoading}
        loadingMessage={() => i18nTShared('loading...')}
        maxMenuHeight={250}
        name='contextSwitcher'
        noOptionsMessage={() =>
          searchParam ? i18nTShared('noMatchingResults') : null
        }
        onChange={handleChange}
        onFocus={() => setIsDomainDivisionsFetchEnabled(true)}
        onInputChange={handleInputChange}
        onMenuClose={() => setIsDomainDivisionsFetchEnabled(false)}
        onMenuScrollToBottom={() => {
          if (hasNextDomainDivisionsPage && !isDomainDivisionsFetching) {
            fetchNextDomainDivisionsPage();
          }
        }}
        options={options}
        styles={{
          input: (provided, _state) => ({
            ...provided,
            margin: 0,
            paddingBottom: 0,
            paddingTop: 0,
          }),
        }}
      />
    </div>
  );
}

ContextSwitcher.propTypes = {
  isDashboardAccessible: PropTypes.bool.isRequired,
};

function DropdownIndicator(props) {
  return (
    <components.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen ?
        <ChevronUpIcon
          className='[&_polygon]:tw-fill-grey-100'
          height={20}
          width={20}
        />
      : <ChevronDownIcon
          className='[&_polygon]:tw-fill-grey-100'
          height={20}
          width={20}
        />
      }
    </components.DropdownIndicator>
  );
}

function Option(props) {
  return (
    <components.Option {...props}>
      <div className='context-switcher__option-item'>
        {props.data.icon && (
          <div className='context-switcher__option-icon'>{props.data.icon}</div>
        )}
        <span className='context-switcher__option-text tw-text-m'>
          {props.data.label}
        </span>
      </div>
      {props.data.isCurrent && (
        <div className='context-switcher__option-item'>
          <span className='context-switcher__current-icon'>
            <AddedIcon
              className='[&_circle]:tw-fill-cyan-400'
              height={16}
              width={16}
            />
          </span>
        </div>
      )}
    </components.Option>
  );
}

function Placeholder(props) {
  const {
    division: {
      attributes: { name: currentDivisionName },
    },
  } = useCurrentActor();

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  return (
    <components.Placeholder {...props}>
      {props.selectProps.menuIsOpen ?
        <div>{i18nTShared('search')}</div>
      : <div>
          {window.location.pathname === dashboardPath ?
            i18nTShared('dashboard')
          : currentDivisionName}
        </div>
      }
    </components.Placeholder>
  );
}
